import useI18n from 'Hooks/useI18n'
import { useRouter } from 'next/navigation'
import TipsyButton from '@/components/ui-kit/TipsyButton'
import { useState } from 'react'
import { deleteUser } from '@/apis/user'
import { useSetRecoilState } from 'recoil'
import { userState } from '@/store/user'
import { removeAuthToken } from '@/lib/auth/jwt'
import { message } from 'antd'
import CommonModal from '@/components/modal/CommonModal'
import TipsyModal from './TipsyModal'
import DeleteReasonModal from './DeleteReasonModal'
import { APP_HOME_PATHNAME } from 'Types/common'
import { withLocale } from '@/locales'
import { getPlatform } from 'Utils/platform'
import {
  buildDeleteUserReq,
  type DeleteReasonSelection,
} from '@/utils/deleteAccountReason'
import type { DeleteUserReq } from '@/apis/user'
import { GuideRegistryKey } from '@/guide/guideRegistry'
import { useGuideStatusActions } from '@/store/guideStatus'

export default function DeleteAccont({
  open,
  onCancel,
}: {
  open: boolean
  onCancel: () => void
}) {
  const { t, lang } = useI18n()
  const router = useRouter()
  const [step, setStep] = useState<'reason' | 'notice' | 'confirm'>('reason')
  const [deleteReason, setDeleteReason] = useState<DeleteUserReq>()
  const setUser = useSetRecoilState(userState)
  const { setLocal } = useGuideStatusActions()

  const handleReasonNext = (reason: DeleteReasonSelection) => {
    const platform = getPlatform()
    setDeleteReason(
      buildDeleteUserReq(reason, {
        platform,
        deviceInfo: typeof navigator !== 'undefined' ? navigator.userAgent : '',
      })
    )
    setStep('notice')
  }

  const handleNoticeNext = () => {
    setStep('confirm')
  }

  const handleConfirm = async () => {
    try {
      if (!deleteReason) {
        message.error(t('Delete failed'))
        return
      }

      await deleteUser(deleteReason)

      message.success(t('Account deleted successfully'))
      removeAuthToken()
      setUser(null)
      setLocal(GuideRegistryKey.SUBSCRIPTION_NEW_USER_MODAL_SHOWN, false)
      setStep('reason')
      setDeleteReason(undefined)
      router.push(withLocale(APP_HOME_PATHNAME, lang))
    } catch (err) {
      message.error(t('Delete failed'))
    }
  }

  const handleCancel = () => {
    setStep('reason')
    setDeleteReason(undefined)
    onCancel()
  }

  const handleNoticeCancel = () => {
    setStep('reason')
  }

  return (
    <>
      {/* 步骤1: 删除原因收集 */}
      <DeleteReasonModal
        open={open && step === 'reason'}
        onCancel={handleCancel}
        onNext={handleReasonNext}
      />

      {/* 步骤2: 删除须知 */}
      <CommonModal
        open={open && step === 'notice'}
        footer={null}
        isLoading={false}
        onCancel={handleNoticeCancel}
        width={500}
      >
        <div className="flex h-full flex-col overflow-auto">
          <div className="mx-auto text-base">{t('Delete Account')}</div>
          <div className="mt-2 px-4">
            <h2 className="mb-3 text-[15px] font-medium">
              {t('Important Notice: Account Deletion is Permanent')}
            </h2>

            <p className="mb-3 text-[13px] leading-4 text-white/70">
              {t(
                'Before you proceed with deleting your account, we urge you to carefully consider the consequences'
              )}{' '}
              {t(
                'Account deletion is not the same as logging out, and once completed, it cannot be undone'
              )}{' '}
              {t('Please take a moment to review the following points:')}
            </p>

            {/* 警告列表 */}
            <div className="space-y-2">
              <div className="flex gap-2">
                <div className="mt-2 h-1.5 w-1.5 rounded-full bg-white" />
                <div className="flex-1 text-sm">
                  <span className="font-bold">{t('Irreversible Loss:')}</span>{' '}
                  {t('Once your account is deleted, it cannot be recovered')}{' '}
                  {t(
                    'You will no longer be able to log in or use any of our products and services associated with this account'
                  )}
                </div>
              </div>

              <div className="flex gap-2">
                <div className="mt-2 h-1.5 w-1.5 rounded-full bg-white" />
                <div className="flex-1 text-sm">
                  <span className="font-bold">
                    {t('Permanent Data Deletion:')}
                  </span>{' '}
                  {t(
                    'All your personal data, in-game assets, favorites, memories, search history, chat logs with characters, interaction data, and order records will be permanently erased'
                  )}{' '}
                  {t(
                    'Even if you re-register with the same account, these items cannot be retrieved'
                  )}
                </div>
              </div>

              <div className="flex gap-2">
                <div className="mt-2 h-1.5 w-1.5 rounded-full bg-white" />
                <div className="flex-1 text-sm">
                  <span className="font-bold">
                    {t('Loss of Character Ownership:')}
                  </span>{' '}
                  {t(
                    'Upon account deletion, you will automatically relinquish ownership of any characters you created'
                  )}{' '}
                  {t(
                    'The creator of these characters will be displayed as "Deleted Account"'
                  )}
                </div>
              </div>

              <div className="flex gap-2">
                <div className="mt-2 h-1.5 w-1.5 rounded-full bg-white" />
                <div className="flex-1 text-sm">
                  <span className="font-bold">
                    {t('Immediate Termination:')}
                  </span>{' '}
                  {t(
                    'Confirming account deletion will result in the immediate termination of your account, with no possibility of recovery'
                  )}
                </div>
              </div>
            </div>

            <p className="mb-8 mt-3 text-[13px] leading-4 text-white/70">
              {t(
                "Do you really want to erase all the memories you've created here? Please think carefully before proceeding with this permanent action"
              )}
            </p>

            <TipsyButton
              className="mb-3 w-full rounded-full bg-white/20 py-3 text-white"
              onClick={handleNoticeNext}
            >
              {t('Permanently Delete')}
            </TipsyButton>
          </div>
        </div>
      </CommonModal>

      {/* 步骤3: 最终确认 */}
      <TipsyModal
        open={open && step === 'confirm'}
        onClose={() => setStep('notice')}
        onConfirm={handleConfirm}
        title={t('Permanently Delete')}
        desc={
          <span className="whitespace-pre-line">
            {t(
              'Your account will be deactivated for 30 days. After that, it will be permanently deleted. Log in within 30 days to cancel.'
            )}
          </span>
        }
        cancelText={t('Cancel')}
        confirmText={t('Delete')}
      />
    </>
  )
}
